import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Modal } from "react-bootstrap";
import { logisticsLinkToBookParams } from "../../../../../Backend/LogisticsRequests";
import { hasValue } from "../../../../../Helpers/validations/common";
import {
  bookNumberMask,
  displayDate,
  getRandomNumber,
} from "../../../../../Helpers/utils/utils";
import TableTemplate from "../../../../../Components/Table/TableTemplate";
import {
  SCHOOL_RETURNS_BY_SCHOOL,
  SCHOOL_RETURNS_BY_STUDENT,
} from "../../../../../Constants/Endpoints";
import SchoolSelection from "../../../../../Components/SchoolSelection/SchoolSelection";
import "./LinkToBookModal.scss";

export function LinkToBookModal({ logisticsPackage, line, submit, ...props }) {
  const tableStudentsRef = useRef(null);
  const tableBooksRef = useRef(null);
  const [t] = useTranslation();
  const [showSelections, setShowSelections] = useState(!line.barcodeBook);
  const [schoolSelected, setSchoolSelected] = useState();
  const [schoolSelection, setSchoolSelection] = useState(false);

  const [studentSelected, setStudentSelected] = useState();
  const [studentSelection, setStudentSelection] = useState(false);
  const [highlightStudentValues, setHighlightStudentValues] = useState([]);

  const [bookSelected, setBookSelected] = useState();
  const [bookSelection, setBookSelection] = useState(false);
  const [highlightBookValues, setHighlightBookValues] = useState([]);

  const [highlightKey] = useState("id");

  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    clearStudentSelected();
  }, [schoolSelected]);

  useEffect(() => {
    clearBookSelected();
  }, [studentSelected]);

  const clearStudentSelected = () => {
    setStudentSelected();
    setHighlightStudentValues([]);
  };

  const clearBookSelected = () => {
    setBookSelected();
    setHighlightBookValues([]);
  };

  const logisticsLinkToBook = async () => {
    const resp = await logisticsLinkToBookParams(line.id, bookSelected?.id);

    if (resp.success) {
      submit();
    }
  };

  const lineInfo = [
    { key: t("Sku"), value: logisticsPackage.logisticId },
    {
      key: t("ReceiptDate"),
      value: hasValue(logisticsPackage.receiptDate)
        ? displayDate(new Date(logisticsPackage.receiptDate).toString())
        : "",
    },
    {
      key: t("InboundLineRef"),
      value: logisticsPackage.inbound.vendorShipmentNo,
    },
    { key: t("Defective"), value: logisticsPackage.inbound.senderName },
    { key: t("Quantity"), value: logisticsPackage.inbound.senderEmail },
  ];

  const bookInfo = [
    { key: t("Title"), value: line.barcodeBook?.book?.title },
    { key: t("BookNumber"), value: line.barcodeBook?.book?.bookNumber },
    { key: t("ISBN"), value: line.barcodeBook?.book?.isbn },
  ];

  const renderInfoRow = (x, key) => {
    return (
      <div className="info-row" key={"info-" + getRandomNumber() + "-" + key}>
        <div className="key">{x.key}:</div>
        <div className="value">{hasValue(x.value) ? x.value : "-"}</div>
      </div>
    );
  };

  const studentColumns = [
    {
      name: "barcode",
      headerCaption: t("Barcode"),
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "student.firstName",
      filterName: "user.firstName",
      sortName: "user.firstName",
      headerCaption: t("Name"),
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "student.preposition",
      filterName: "user.preposition",
      sortName: "user.preposition",
      headerCaption: t("Preposition"),
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "student.lastName",
      filterName: "user.lastName",
      sortName: "user.lastName",
      headerCaption: t("LastName"),
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "actions",
      headerCaption: "",
      size: "small",
      textAlign: "center",
      canSort: false,
      canFilter: false,
      canEdit: false,
      caption: (x) => {
        return (
          <Button
            className="button-selected"
            disabled={highlightStudentValues[0] === x.id}
            onClick={() => {
              setStudentSelected(x);
              setHighlightStudentValues([x.id]);
              setStudentSelection(false);
            }}
          >
            {highlightStudentValues[0] === x.id ? t("Selected") : t("Select")}
          </Button>
        );
      },
    },
  ];

  const bookColumns = [
    {
      name: "book.title",
      headerCaption: t("Title"),
      size: "large",
      canSort: false,
      canFilter: false,
      canEdit: false,
    },
    {
      name: "book.isbn",
      headerCaption: t("ISBN"),
      size: "small",
      canSort: false,
      canFilter: false,
      canEdit: false,
    },
    {
      name: "book.bookNumber",
      headerCaption: t("BookNumber"),
      size: "small",
      textAlign: "right",
      caption: (x) => bookNumberMask(x.book.bookNumber),
      canSort: false,
      canFilter: false,
      canEdit: false,
    },
    {
      name: "actions",
      headerCaption: "",
      size: "small",
      textAlign: "center",
      canSort: false,
      canFilter: false,
      canEdit: false,
      caption: (x) => {
        return (
          <Button
            className="button-selected"
            disabled={highlightBookValues[0] === x.id}
            onClick={() => {
              setBookSelected(x);
              setHighlightBookValues([x.id]);
              setBookSelection(false);
            }}
          >
            {highlightBookValues[0] === x.id ? t("Selected") : t("Select")}
          </Button>
        );
      },
    },
  ];

  const closeAllSelections = () => {
    setSchoolSelection(false);
    setStudentSelection(false);
    setBookSelection(false);
  };

  const getBookFromPackage = (bookId, lp) => {
    return lp.barcode.barcodeBooks.findIndex((b) => b.book.id === bookId);
  };

  const fromPackage =
    line &&
    line.barcodeBook &&
    getBookFromPackage(line.barcodeBook.book.id, logisticsPackage) !== -1;

  return (
    <Modal {...props} centered dialogClassName="link-to-book-modal">
      <Modal.Header className="link-to-book-modal-header" closeButton>
        <h2 className="title">{t("Line")}</h2>
      </Modal.Header>
      <Modal.Body className="link-to-book-modal-content">
        <div className="info-wrapper">
          <div className="line-wrapper">
            <div className="title-wrapper"></div>
            {lineInfo.map((x) => renderInfoRow(x, "line"))}
          </div>

          {line && line.barcodeBook ? (
            <div className="book-wrapper">
              <div className="title-wrapper">
                <h2 className="title">{t("Book")}</h2>
                <Button
                  className="button-book-change"
                  onClick={() => setShowSelections(true)}
                >
                  {t("Change")}
                </Button>
              </div>
              {bookInfo.map((x) => renderInfoRow(x, "book"))}
              <div
                className={`banner ${
                  fromPackage ? `from-package` : `not-from-package`
                }`}
              >
                {fromPackage ? t("BookFromPackage") : t("BookNotFromPackage")}
              </div>
            </div>
          ) : (
            <div className="book-not-wrapper">
              <div className="book-not-title">{t("BookNotLinked")} </div>
              <div className="book-not-text">{t("BookNotLinkedText")} </div>
            </div>
          )}
        </div>

        {showSelections && (
          <React.Fragment>
            <SchoolSelection
              schoolSelected={schoolSelected}
              schoolSelection={schoolSelection}
              onClickChange={() => {
                if (schoolSelection) {
                  setSchoolSelection(false);
                } else {
                  closeAllSelections();
                  setSchoolSelection(true);
                }
              }}
              setSchoolSelection={setSchoolSelection}
              setSchoolSelected={setSchoolSelected}
            />

            <div className="selection-wrapper">
              <div className="banner-wrapper">
                <div className="banner-wrapper-title-wrapper">
                  <div className="banner-wrapper-title">{t("Student")}</div>
                  <div className="banner-wrapper-subtitle">
                    {studentSelected
                      ? `${studentSelected.student.firstName} ${studentSelected.student.preposition} ${studentSelected.student.lastName}`
                      : t("NotSelected")}
                  </div>
                </div>
                <Button
                  className="button-banner"
                  disabled={!hasValue(schoolSelected?.id)}
                  onClick={() => {
                    if (studentSelection) {
                      setStudentSelection(false);
                    } else {
                      closeAllSelections();
                      setStudentSelection(true);
                    }
                  }}
                >
                  {highlightStudentValues.length === 0
                    ? t("Select")
                    : t("Change")}
                </Button>
              </div>
              <div
                className={`table-selection-wrapper${
                  studentSelection ? ` show` : ``
                }`}
              >
                {studentSelection && (
                  <TableTemplate
                    ref={tableStudentsRef}
                    title={t("Students")}
                    pageSize={5}
                    highlightValues={highlightStudentValues}
                    highlightKey={highlightKey}
                    endpoint={
                      SCHOOL_RETURNS_BY_SCHOOL +
                      "?schoolId=" +
                      schoolSelected?.id
                    }
                    extraFilters={[
                      {
                        name: "year",
                        headerCaption: t("Year"),
                        value: year,
                        component: (
                          <Form.Control
                            value={year}
                            onChange={(x) => setYear(x.target.value)}
                          />
                        ),
                      },
                    ]}
                    columns={studentColumns}
                    uniqueId="id"
                    paginator={true}
                    showSizeSelector={false}
                  />
                )}
              </div>
            </div>

            <div className="selection-wrapper">
              <div className="banner-wrapper">
                <div className="banner-wrapper-title-wrapper">
                  <div className="banner-wrapper-title">{t("Book")}</div>
                  <div className="banner-wrapper-subtitle">
                    {bookSelected ? bookSelected.book.title : t("NotSelected")}
                  </div>
                </div>
                <Button
                  className="button-banner"
                  disabled={highlightStudentValues.length === 0}
                  onClick={() => {
                    if (bookSelection) {
                      setBookSelection(false);
                    } else {
                      closeAllSelections();
                      setBookSelection(true);
                    }
                  }}
                >
                  {highlightBookValues.length === 0 ? t("Select") : t("Change")}
                </Button>
              </div>
              <div
                className={`table-selection-wrapper${
                  bookSelection ? ` show` : ``
                }`}
              >
                {bookSelection && (
                  <TableTemplate
                    ref={tableBooksRef}
                    title={t("Books")}
                    pageSize={20}
                    highlightValues={highlightBookValues}
                    highlightKey={highlightKey}
                    endpoint={SCHOOL_RETURNS_BY_STUDENT}
                    columns={bookColumns}
                    uniqueId="isbn"
                    paginator={true}
                    showSizeSelector={false}
                    extraFilters={[
                      {
                        name: "barcodeId",
                        headerCaption: t("Barcode"),
                        value: studentSelected.id,
                        hide: true,
                      },
                    ]}
                  />
                )}
              </div>
            </div>

            <div className="button-wrapper">
              <Button
                className="button"
                disabled={!hasValue(bookSelected?.id)}
                onClick={() => logisticsLinkToBook()}
              >
                {t("Confirm")}
              </Button>
            </div>
          </React.Fragment>
        )}
      </Modal.Body>
    </Modal>
  );
}
